import type { StateCreator } from 'zustand';
import { addPlayerGroups, type User } from '@/api/auth';
import { groupMember } from '@/helpers/user';
import {
  type ISSEInitEvent,
  subscribeLoyaltySse,
} from '@/modules/loyaltyNew/api/loyalty-sse';
import type { CommonStoreType, LoyaltySseState } from '../types';

type SseState = NonNullable<LoyaltySseState['loyaltySseState']>;

interface LevelTransitionData {
  toLevel: string;
  points: number;
  upgradePointAmount: number;
  retentionDeadline: string;
  progressStartedAt: string;
}

const LOYALTY_LEVEL_GROUP_PREFIX = 'loyalty_level:';

/**
 * Assigns the user to the correct loyalty group when the subscription-provided
 * level number differs from their current group. No-op if already in sync.
 */
async function syncLoyaltyLevelGroup(
  user: User | undefined,
  levelNumber: number
): Promise<void> {
  if (!user?.id) return;
  const targetGroup = `${LOYALTY_LEVEL_GROUP_PREFIX}${levelNumber}`;
  if (groupMember(targetGroup)(user)) return;

  await addPlayerGroups([targetGroup]);
  console.log(
    `[syncLoyaltyLevelGroup] User ${user?.id}: assigned to "${targetGroup}"`
  );
}

function findLevel(levels: SseState['levels'], name: string) {
  const found = levels.find((l) => l.name === name);
  return found ? { id: found.id, number: found.id, name: found.name } : null;
}

function patchSseState(state: CommonStoreType, patch: Partial<ISSEInitEvent>) {
  if (!state.loyaltySseState) return {};
  return { loyaltySseState: { ...state.loyaltySseState, ...patch } };
}

function applyLevelTransition(
  state: CommonStoreType,
  {
    toLevel,
    points,
    upgradePointAmount,
    retentionDeadline,
    progressStartedAt,
  }: LevelTransitionData
) {
  if (!state.loyaltySseState) return {};
  const newLevel = findLevel(state.loyaltySseState.levels, toLevel);
  return patchSseState(state, {
    level: newLevel ?? state.loyaltySseState.level,
    points,
    pointsToNextLevel: upgradePointAmount - points,
    retentionDeadline,
    progressStartedAt,
  });
}

function patchBonusActivated(
  state: CommonStoreType,
  levelId: number,
  activated: string
) {
  if (!state.loyaltySseState) return {};
  const levels = state.loyaltySseState.levels.map((level, index) => {
    if (index !== levelId - 1 || !level.bonus) return level;
    return { ...level, bonus: { ...level.bonus, activated } };
  });
  return { loyaltySseState: { ...state.loyaltySseState, levels } };
}

export const useLoyaltySseSlice: StateCreator<
  CommonStoreType,
  [],
  [],
  LoyaltySseState
> = (set, get) => {
  let unsubscribe: (() => void) | null = null;

  /** Applies a level transition and optionally syncs the user's loyalty group. */
  function handleLevelTransition(
    data: LevelTransitionData,
    syncGroup: boolean
  ): void {
    set((state) => applyLevelTransition(state, data));

    if (!syncGroup) return;

    const { user, loyaltySseState } = get();
    const newLevel = findLevel(loyaltySseState?.levels ?? [], data.toLevel);
    if (newLevel) {
      syncLoyaltyLevelGroup(user, newLevel.number).catch((err) =>
        console.error('[useLoyaltySseSlice] onUpgrade group sync failed:', err)
      );
    }
  }

  return {
    loyaltySseState: null,
    loyaltySseIsLoading: true,

    subscribeLoyaltySse: () => {
      if (typeof window === 'undefined') return;
      if (unsubscribe) return;

      unsubscribe = subscribeLoyaltySse({
        onInit: (initData) => {
          set({ loyaltySseState: initData, loyaltySseIsLoading: false });
          if (initData.level?.number) {
            syncLoyaltyLevelGroup(get().user, initData.level.number).catch(
              (err) =>
                console.error(
                  '[useLoyaltySseSlice] onInit group sync failed:',
                  err
                )
            );
          }
        },
        onProgress: ({ points, pointsToNextLevel }) => {
          set((state) => patchSseState(state, { points, pointsToNextLevel }));
        },
        onUpgrade: (data) => handleLevelTransition(data, true),
        onDowngrade: (data) => handleLevelTransition(data, false),
        onRetain: ({ points, retentionDeadline, progressStartedAt }) => {
          set((state) =>
            patchSseState(state, {
              points,
              retentionDeadline,
              progressStartedAt,
            })
          );
        },
        onBonus: ({ levelId, activated }) => {
          set((state) => patchBonusActivated(state, levelId, activated));
        },
      });
    },

    unsubscribeLoyaltySse: () => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
        set({ loyaltySseState: null, loyaltySseIsLoading: true });
      }
    },
  };
};
